import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/Table";
import { UsersThree, Plus, Trash, FloppyDisk, X } from "@phosphor-icons/react";

export default function GroupManager() {
  const [groups, setGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [name, setName] = useState("");
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null); // group id being edited
  const [editName, setEditName] = useState("");
  const [editSel, setEditSel] = useState([]);

  const load = () => api.get("/groups").then((r) => setGroups(r.data.groups)).catch(() => {});
  useEffect(() => { load(); api.get("/vehicles").then((r) => setVehicles(r.data.vehicles)).catch(() => {}); }, []);

  const vname = (id) => vehicles.find((v) => v.id === id)?.vehicle_number || id;

  const create = async () => {
    if (!name.trim()) return toast.error("Group name required");
    if (!sel.length) return toast.error("Select at least one vehicle");
    try { await api.post("/groups", { name: name.trim(), vehicle_ids: sel }); toast.success("Group created"); setName(""); setSel([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (g) => { setEditing(g.id); setEditName(g.name); setEditSel(g.vehicle_ids || []); };

  const saveEdit = async (gid) => {
    try { await api.put(`/groups/${gid}`, { name: editName.trim(), vehicle_ids: editSel }); toast.success("Group updated"); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (gid) => {
    try { await api.delete(`/groups/${gid}`); toast.success("Group deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const VehiclePicker = ({ value, onChange, testid }) => (
    <select multiple data-testid={testid} value={value}
      onChange={(e) => onChange(Array.from(e.target.selectedOptions).map((o) => o.value))}
      className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm h-40">
      {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
    </select>
  );

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Vehicle Groups" subtitle="Build groups once, then filter the live map and reports in one click" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Create */}
        <div className="bg-card border border-border rounded-sm p-4 space-y-2 h-fit">
          <div className="label-caps flex items-center gap-1.5"><Plus size={13} /> New Group</div>
          <input data-testid="group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name (e.g. Mumbai Trucks)"
            className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
          <div className="label-caps text-[9px] pt-1">Vehicles <span className="normal-case">(ctrl/cmd for multiple)</span></div>
          <VehiclePicker value={sel} onChange={setSel} testid="group-vehicles" />
          <button data-testid="group-create" onClick={create} className="w-full bg-primary text-white font-semibold text-sm py-2 rounded-sm">Create Group</button>
        </div>

        {/* List */}
        <div className="lg:col-span-2 space-y-3" data-testid="group-list">
          {groups.length === 0 && <div className="bg-card border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">No groups yet. Create your first group on the left.</div>}
          {groups.map((g) => (
            <div key={g.id} className="bg-card border border-border rounded-sm p-4" data-testid={`group-${g.id}`}>
              {editing === g.id ? (
                <div className="space-y-2">
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm" />
                  <VehiclePicker value={editSel} onChange={setEditSel} testid={`edit-vehicles-${g.id}`} />
                  <div className="flex gap-2">
                    <button data-testid={`group-save-${g.id}`} onClick={() => saveEdit(g.id)} className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-3 py-1.5 rounded-sm"><FloppyDisk size={14} /> Save</button>
                    <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-1.5 rounded-sm"><X size={14} /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><UsersThree size={18} weight="bold" className="text-primary" /><span className="font-display font-bold">{g.name}</span>
                      <span className="text-xs text-muted-foreground">{g.vehicle_ids.length} vehicles</span></div>
                    <div className="flex gap-1">
                      <button data-testid={`group-edit-${g.id}`} onClick={() => startEdit(g)} className="text-xs font-semibold px-2 py-1 border border-border rounded-sm hover:border-primary">Edit</button>
                      <button data-testid={`group-delete-${g.id}`} onClick={() => remove(g.id)} className="text-muted-foreground hover:text-destructive p-1"><Trash size={15} weight="bold" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.vehicle_ids.slice(0, 30).map((id) => <span key={id} className="text-xs bg-background border border-border rounded-sm px-2 py-0.5">{vname(id)}</span>)}
                    {g.vehicle_ids.length === 0 && <span className="text-xs text-muted-foreground">No vehicles</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
